import { useEffect, useState } from "react";
import PageHeader from "../../component/pageHeaders";
import Loadingbox from "../../component/loadingbox";
import { API } from "../../config/API";
import { POST } from "../../utils/apirequest";
import { Button, DatePicker, Input, Tag, message } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import DataTable from "./components/dataTable";
import CreateModal from "./components/createModal";
import ViewModal from "./components/viewModal";

const { RangePicker } = DatePicker;

function ReleaseNotificationsScreen() {
  const [data, setData] = useState<any[]>([]);
  const [meta, setMeta] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [indicator, setIndicator] = useState<boolean>(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewItem, setViewItem] = useState<any>(null);

  useEffect(() => {
    loadData(1, pageSize, search, dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (
    pg: number,
    ps: number,
    searchText?: string,
    range?: [Dayjs | null, Dayjs | null] | null
  ) => {
    try {
      setIndicator(true);
      const body: any = {
        page: pg,
        pageSize: ps,
      };
      if (searchText) body.search = searchText;
      if (range && range[0]) body.startDate = range[0].format("YYYY-MM-DD");
      if (range && range[1]) body.endDate = range[1].format("YYYY-MM-DD");

      const res: any = await POST(API.RELEASE_NOTIFICATIONS_LIST, body);
      if (res?.status) {
        setData(res?.data || []);
        setMeta(res?.total || 0);
      } else {
        setData([]);
        setMeta(0);
      }
    } catch (err) {
      console.error("Failed to load release notifications", err);
      message.error("Couldn't load notifications");
    } finally {
      setIsLoading(false);
      setIndicator(false);
    }
  };

  let searchTimer: any = null;
  const onSearch = (val: string) => {
    setSearch(val);
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setPage(1);
      loadData(1, pageSize, val, dateRange);
    }, 500);
  };

  const onDateChange = (dates: any) => {
    const range: [Dayjs | null, Dayjs | null] | null = dates
      ? [dates[0] || null, dates[1] || null]
      : null;
    setDateRange(range);
    setPage(1);
    loadData(1, pageSize, search, range);
  };

  const clearDates = () => {
    setDateRange(null);
    setPage(1);
    loadData(1, pageSize, search, null);
  };

  const clearSearch = () => {
    setSearch("");
    setPage(1);
    loadData(1, pageSize, "", dateRange);
  };

  const clearAll = () => {
    setSearch("");
    setDateRange(null);
    setPage(1);
    loadData(1, pageSize, "", null);
  };

  const onPageChange = (pg: number, ps: number) => {
    window.scrollTo(0, 0);
    setPage(pg);
    setPageSize(ps);
    loadData(pg, ps, search, dateRange);
  };

  const refresh = () => loadData(page, pageSize, search, dateRange);

  const startDate = dateRange ? dateRange[0] : null;
  const endDate = dateRange ? dateRange[1] : null;
  const hasFilters = !!(search || startDate || endDate);

  return (
    <div>
      <PageHeader
        title={"Release Notifications"}
        loader={indicator}
        length={`Dashboard / Release Notifications`}
      >
        <div className="homescreen-pageItem">
          <Input
            style={{ width: "260px" }}
            allowClear
            placeholder="Search by subject"
            value={search}
            onChange={(e: any) => onSearch(e.target.value)}
          />
          <div style={{ width: 10 }} />
          <RangePicker
            value={dateRange as any}
            onChange={onDateChange}
            allowClear
            style={{ width: 260 }}
          />
          <div style={{ width: 10 }} />
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            Create +
          </Button>
        </div>
      </PageHeader>
      <div style={{ padding: 5 }} />
      <div className="PageHeaders-box5">
        <div className="PageHeaders-txt3">Notifications ( {meta} )</div>
        {hasFilters ? (
          <>
            <div>|</div>
            <div className="PageHeaders-txt2">Filter By : </div>
            {search ? (
              <Tag onClick={clearSearch} style={{ cursor: "pointer" }}>
                search : {search} <CloseCircleOutlined />
              </Tag>
            ) : null}
            {startDate || endDate ? (
              <Tag onClick={clearDates} style={{ cursor: "pointer" }}>
                date : {startDate ? startDate.format("DD-MM-YYYY") : "..."} ~{" "}
                {endDate ? endDate.format("DD-MM-YYYY") : "..."}{" "}
                <CloseCircleOutlined />
              </Tag>
            ) : null}
            <Tag color="red" onClick={clearAll} style={{ cursor: "pointer" }}>
              Clear All
            </Tag>
          </>
        ) : null}
      </div>

      {isLoading ? (
        <Loadingbox />
      ) : (
        <DataTable
          data={data}
          meta={meta}
          page={page}
          pagesize={pageSize}
          onPageChange={onPageChange}
          onView={(item: any) => setViewItem(item)}
        />
      )}

      {createOpen ? (
        <CreateModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSuccess={() => {
            setCreateOpen(false);
            refresh();
          }}
        />
      ) : null}

      {viewItem ? (
        <ViewModal
          open={!!viewItem}
          item={viewItem}
          onClose={() => setViewItem(null)}
        />
      ) : null}
    </div>
  );
}

export default ReleaseNotificationsScreen;
